import React, { useState } from 'react';
import { 
  Menu, X, ArrowLeft, Mail, Phone, MapPin, Clock, 
  Send, MessageCircle, Heart, Shield, Users, 
  CheckCircle, Star, Download, Smartphone
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

const ContactPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e:any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Thank you for your message! We\'ll get back to you soon.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Us",
      details: "team@beternal.life",
      description: "Send us an email anytime"
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Us",
      details: "+1 (555) 123-4567",
      description: "Mon-Fri 9AM-6PM EST"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Visit Us",
      details: "123 Legacy Lane, Heritage City, HC 12345",
      description: "Our headquarters"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Business Hours",
      details: "Mon-Fri: 9AM-6PM EST",
      description: "We're here to help"
    }
  ];

  const supportOptions = [
    // {
    //   icon: <MessageCircle className="w-8 h-8" />,
    //   title: "Live Chat Support",
    //   description: "Get instant help from our support team",
    //   action: "Start Chat",
    //   available: true
    // },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Email Support",
      description: "Send us detailed questions via email",
      action: "Send Email",
      available: true
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Phone Support",
      description: "Speak directly with our experts",
      action: "Call Now",
      available: true
    }
  ];

  const faqs = [
    {
      question: "How quickly will I receive a response?",
      answer: "We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call our support line."
    },
    {
      question: "What information should I include in my message?",
      answer: "Please include as much detail as possible about your question or issue. If you're experiencing technical problems, include your account email and device information."
    },
    {
      question: "Do you offer phone consultations?",
      answer: "Yes! We offer free 30-minute consultations to help you understand how bETERNAL can best serve your family's legacy needs."
    },
    {
      question: "Can I schedule a demo?",
      answer: "Absolutely! We'd love to show you how bETERNAL works. Contact us to schedule a personalized demonstration."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-lg border-b border-orange-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
          <div className="flex items-center space-x-3">
              <NavLink to={'/'} >
                <img src="images/log.svg" />
              </NavLink>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-10">
              <a href="/" className="text-gray-700 hover:text-orange-500 font-semibold text-lg  pb-1">Home</a>
              <a href="features" className="text-gray-700 hover:text-orange-500 transition-colors text-lg font-medium">Features</a>
              <a href="pricing" className="text-gray-700 hover:text-orange-500 transition-colors text-lg font-medium">Package</a>
              <a href="contact-us" className="text-orange-500 transition-colors border-b-2 border-orange-500 text-lg font-medium">Contact</a>
              <a href='sign-in' className="text-orange-500 hover:text-orange-600 transition-colors text-lg font-medium">Sign in</a>
              <a href='register' className="bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl">
                Sign up
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-orange-100 shadow-lg">
            <div className="px-6 py-6 space-y-4">
              <a href="/" className="block py-3 text-grey-700 font-semibold text-xl">Home</a>
              <a href="features" className="block py-3 text-gray-700 text-xl">Features</a>
              <a href="pricing" className="block py-3 text-gray-700 text-xl">Package</a>
              <a href="contact-us" className="block py-3 text-orange-500 text-xl">Contact</a>
              <div className="flex flex-col space-y-3 pt-4">
                <a href='/sign-in' className="text-orange-500 text-xl font-medium text-left">Sign in</a>
                <a href='/register' className="bg-orange-500 text-white px-6 py-3 rounded-xl text-lg font-semibold w-full">Sign up</a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Contact
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              bEternal Today
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
            Join bETERNAL today and embark on a journey that celebrates the richness of your past while propelling you towards an empowered future.
          </p>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            Because your legacy isn't just a memory – it's a guiding light for the generations yet to come.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="Enter your first name..."
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="Enter your last name..."
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    placeholder="Enter your email address..."
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    placeholder="How can we help you?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                    placeholder="Tell us about your needs or questions..."
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{info.title}</h3>
                      <p className="text-gray-900 font-medium">{info.details}</p>
                      <p className="text-gray-600 text-sm">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Support Options */}
              <h3 className="text-xl font-bold text-gray-900 mb-6">Support Options</h3>
              <div className="space-y-4">
                {supportOptions.map((option, index) => (
                  <div key={index} className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-2xl border border-orange-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center text-white">
                          {option.icon}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{option.title}</h4>
                          <p className="text-gray-600 text-sm">{option.description}</p>
                        </div>
                      </div>
                      {/* <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors">
                        {option.action}
                      </button> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-1 rounded-3xl">
                  <div className="bg-white rounded-3xl p-8">
                    <img 
                      src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=600&fit=crop"
                      alt="Vacation memory"
                      className="w-full h-64 object-cover rounded-2xl mb-4"
                    />
                    <div className="bg-orange-500 text-white p-4 rounded-2xl">
                      <h3 className="text-lg font-bold mb-2">Vacation at grandma's</h3>
                      <p className="text-sm opacity-90">October 8, 1996</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Coming soon
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Download
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                  bEternal Mobile App
                </span>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Take your memories with you everywhere. Our mobile app makes it easy to capture, store, and share your most precious moments on the go.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 flex items-center space-x-3 cursor-pointer hover:bg-white/20 transition-colors">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-black font-bold text-xs">📱</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Download on the</p>
                    <p className="font-semibold">App Store</p>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 flex items-center space-x-3 cursor-pointer hover:bg-white/20 transition-colors">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-black font-bold text-xs">📱</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Get it on</p>
                    <p className="font-semibold">Google Play</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4 text-gray-400">
                <Smartphone className="w-5 h-5" />
                <span className="text-sm">Available for iOS and Android devices</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Find answers to common questions about contacting our team
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">Still have questions?</p>
            <button className="bg-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg">
              Schedule a Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Stay Connected</h2>
          <p className="text-lg text-gray-600 mb-8">
            Be remembered. Stay Connected With bETERNAL
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <button className="bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors">
                Subscribe Now!
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Get updates, tips, and stories about preserving your digital legacy
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-lg">b</span>
                </div>
                <span className="text-2xl font-bold">ETERNAL</span>
              </div>
              <p className="text-gray-400 text-lg font-light">YOUR LEGACY, YOUR WAY.</p>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Site Link</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="/" className="hover:text-white transition-colors text-lg">Home</a></li>
                <li><a href="features" className="hover:text-white transition-colors text-lg">Features</a></li>
                <li><a href="pricing" className="hover:text-white transition-colors text-lg">Packages</a></li>
                <li><a href="contact-us" className="hover:text-white transition-colors text-lg">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors text-lg">Terms & Conditions</a></li>
                <li><a href="#" className="hover:text-white transition-colors text-lg">Help & Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors text-lg">Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-xl mb-6">Social Media</h4>
              <div className="flex space-x-4">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/share/18tWm4dH8V/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our Facebook page"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/beternal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-500 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@beternal4543?si=LY1iOIxF-jzEaoY9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-red-600 rounded-2xl hover:scale-110 transition-transform cursor-pointer flex items-center justify-center"
                  aria-label="Visit our YouTube channel"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-16 pt-8 text-center text-lg text-gray-400 font-light">
            bETERNAL ® | © 2025 all rights reserved
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;